/**
 * Kaleido predictive search - products only, independent from default predictive-search.
 * Does not use or conflict with assets/predictive-search.js or assets/predictive-search.css.
 */
(function () {
  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  class SearchFormKaleido extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (this.input?.form) {
        this.input.form.addEventListener("reset", this.onFormReset.bind(this));
        this.input.addEventListener(
          "input",
          debounce(() => this.onChange(), 300)
        );
      }
    }

    toggleResetButton() {
      if (!this.resetButton || !this.input) return;
      const hidden = this.resetButton.classList.contains("hidden");
      if (this.input.value.length > 0 && hidden) {
        this.resetButton.classList.remove("hidden");
      } else if (this.input.value.length === 0 && !hidden) {
        this.resetButton.classList.add("hidden");
      }
    }

    onChange() {
      this.toggleResetButton();
    }

    onFormReset(e) {
      e.preventDefault();
      if (
        !document.querySelector(
          'predictive-search-kaleido [aria-selected="true"] a'
        )
      ) {
        this.input.value = "";
        this.input.focus();
        this.toggleResetButton();
      }
    }
  }

  class PredictiveSearchKaleido extends SearchFormKaleido {
    constructor() {
      super();
      this.cachedResults = {};
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = "";

      this.statusElement = this.querySelector(
        ".predictive-search-kaleido-status"
      );
      this.resultsContainer = this.querySelector(
        "[data-predictive-search-kaleido]"
      );
      this.initialRecommendations = this.querySelector(
        ".predictive-search-kaleido-initial-recommendations"
      );
      this.resultsSlot = this.querySelector(
        ".predictive-search-kaleido-results-slot"
      );
      this.loadingEl = this.querySelector(".predictive-search-kaleido-loading");

      this.setupEventListeners();
      this.loadingText = this.getAttribute("data-loading-text") || "Loading...";
    }

    setupEventListeners() {
      if (!this.input?.form) return;
      this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
      this.input.addEventListener("focus", this.onFocus.bind(this));
      this.addEventListener("focusout", this.onFocusOut.bind(this));
    }

    getQuery() {
      return this.input?.value?.trim() || "";
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();

      if (!newSearchTerm.startsWith(this.searchTerm || "")) {
        this.querySelector(
          "#predictive-search-kaleido-results-groups-wrapper"
        )?.remove();
      }
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.showRecommendations();
        return;
      }

      this.hideRecommendations();
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(e) {
      const query = this.getQuery();
      const selectedLink = this.querySelector('[aria-selected="true"] a');

      if (selectedLink) {
        e.preventDefault();
        if (typeof window.closeSearch === "function") window.closeSearch();
        return;
      }

      if (!query.length) {
        e.preventDefault();
        if (typeof window.closeSearch === "function") window.closeSearch();
        return;
      }

      // User submitted with a query (e.g. clicked submit button): navigate to search page with value
      e.preventDefault();
      const form = this.input?.form;
      if (form && form.action) {
        const params = new URLSearchParams(new FormData(form));
        const url = `${form.action}${
          form.action.includes("?") ? "&" : "?"
        }${params.toString()}`;
        if (typeof window.closeSearch === "function") window.closeSearch();
        window.location.href = url;
      }
    }

    onFormReset(e) {
      super.onFormReset(e);
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }

    onFocus() {
      const q = this.getQuery();
      if (!q.length) {
        this.showRecommendations();
        return;
      }
      if (this.searchTerm !== q) {
        this.onChange();
      } else if (this.getAttribute("results") === "true") {
        this.open();
      } else {
        this.getSearchResults(q);
      }
    }

    onFocusOut() {
      // Full-page Kaleido popup: don't close on focusout (recommendations stay visible)
      if (this.closest(".header__search-kaleido")) return;
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    showRecommendations() {
      if (this.resultsSlot) this.resultsSlot.innerHTML = "";
      if (this.initialRecommendations) {
        this.initialRecommendations.classList.remove(
          "kaleido-initially-hidden"
        );
      }
      this.setAttribute("results", "false");
      this.open();
    }

    hideRecommendations() {
      if (this.initialRecommendations) {
        this.initialRecommendations.classList.add("kaleido-initially-hidden");
      }
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/\s/g, "-").toLowerCase();

      this.setLoading(true);
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      // Path-relative URL keeps fetch same-origin (fixes CORS on 127.0.0.1 local dev)
      const url = `/search/suggest?q=${encodeURIComponent(
        searchTerm
      )}&resources[type]=product&section_id=predictive-search-kaleido`;

      fetch(url, { signal: this.abortController.signal })
        .then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, "text/html");
          const section = doc.querySelector(
            "#shopify-section-predictive-search-kaleido"
          );
          const markup = section ? section.innerHTML : "";
          this.cachedResults[queryKey] = markup;
          this.renderSearchResults(markup);
        })
        .catch((err) => {
          if (err?.code === 20) return;
          this.close();
        });
    }

    setLoading(loading) {
      if (this.loadingEl) this.loadingEl.hidden = !loading;
      if (loading) this.setAttribute("loading", "");
      else this.removeAttribute("loading");
    }

    renderSearchResults(markup) {
      if (!this.resultsSlot) return;

      const temp = document.createElement("div");
      temp.innerHTML = markup;

      const sectionResults = temp.querySelector(
        ".predictive-search-kaleido-results"
      );
      const noResults = temp.querySelector(
        ".predictive-search-kaleido__no-results[data-no-results]"
      );

      this.resultsSlot.innerHTML = "";
      this.setLoading(false);

      if (sectionResults && sectionResults.innerHTML.trim()) {
        sectionResults.classList.remove("initially-hidden-p");
        this.resultsSlot.appendChild(sectionResults);
      } else if (noResults) {
        noResults.classList.remove("initially-hidden-p");
        this.resultsSlot.appendChild(noResults);
      }

      this.setAttribute("results", "true");
      this.hideRecommendations();
      this.open();

      const countEl = temp.querySelector(
        "[data-predictive-search-kaleido-live-region-count-value]"
      );
      if (this.statusElement && countEl) {
        this.statusElement.textContent = countEl.textContent;
        this.statusElement.setAttribute("aria-hidden", "false");
        setTimeout(
          () => this.statusElement.setAttribute("aria-hidden", "true"),
          1000
        );
      }
    }

    open() {
      if (!this.resultsContainer) return;
      const rect = this.resultsContainer.getBoundingClientRect();
      const maxH = window.innerHeight - Math.max(0, rect.top);
      this.resultsContainer.style.maxHeight = `${Math.max(200, maxH)}px`;
      this.setAttribute("open", "");
      this.isOpen = true;
      this.input?.setAttribute("aria-expanded", "true");
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm && this.input) {
        this.input.value = "";
        this.removeAttribute("results");
        this.showRecommendations();
      }
      this.querySelector('[aria-selected="true"]')?.setAttribute(
        "aria-selected",
        "false"
      );
      this.input?.setAttribute("aria-activedescendant", "");
      this.removeAttribute("loading");
      this.removeAttribute("open");
      if (this.resultsContainer) this.resultsContainer.removeAttribute("style");
    }
  }

  customElements.define("search-form-kaleido", SearchFormKaleido);
  customElements.define("predictive-search-kaleido", PredictiveSearchKaleido);
})();
